<template>
  <cst-page-template :hide-menu="true">
    <header-menu />
    <div class="lease-mapping-page">
      <div class="page-header">
        <h1>Lease Mapping Beheer</h1>
        <app-button data-qa="lease-mapping-create-button" @click="onCreate">
          <i class="icon-plus"></i>
          Nieuwe mapping toevoegen
        </app-button>
      </div>

      <div class="content-section">
        <div v-if="loading" class="loading">Mappings laden...</div>

        <template v-else-if="mappings.length">
          <div class="table-scroll-container">
            <table class="mappings-table">
              <thead>
                <tr>
                  <th>Dealer ID</th>
                  <th>Debtor ID</th>
                  <th>CRM Nummer</th>
                  <th>Klant ID</th>
                  <th class="actions-col">Acties</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="mapping in mappings" :key="mapping.DealerId + '|' + mapping.DebtorId">
                  <td>{{ mapping.DealerId }}</td>
                  <td>{{ mapping.DebtorId }}</td>
                  <td>{{ mapping.CrmNumber }}</td>
                  <td>{{ mapping.CustomerId || '-' }}</td>
                  <td>
                    <div class="action-buttons">
                      <button
                        class="btn-action btn-delete"
                        title="Verwijderen"
                        :data-qa="`lease-mapping-delete-${mapping.DebtorId}`"
                        @click="onDeleteClick(mapping)"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div v-else class="empty-state">
          <p>Er zijn nog geen handmatige lease mappings.</p>
          <app-button data-qa="lease-mapping-create-first-button" @click="onCreate"
            >Voeg de eerste mapping toe</app-button
          >
        </div>
      </div>

      <lease-mapping-form v-if="showForm" @cancel="closeForm" @saved="onMappingSaved" />

      <!-- Delete confirmation dialog -->
      <div
        v-if="deleteConfirmMapping"
        class="modal-overlay"
        @click.self="deleteConfirmMapping = null"
      >
        <div class="modal-dialog">
          <h3 class="modal-title">Mapping verwijderen</h3>
          <p class="modal-body">Weet je zeker dat je deze mapping wilt verwijderen?</p>
          <div class="modal-actions">
            <app-button
              button-class="inverted--bordered"
              data-qa="lease-mapping-delete-cancel-button"
              @click="deleteConfirmMapping = null"
              >Annuleren</app-button
            >
            <app-button
              button-class="danger"
              data-qa="lease-mapping-delete-confirm-button"
              :disabled="deleteInProgress"
              @click="onDeleteConfirm"
            >
              {{ deleteInProgress ? 'Verwijderen...' : 'Verwijderen' }}
            </app-button>
          </div>
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
        <button class="close-error" @click="error = null">&times;</button>
      </div>
    </div>
  </cst-page-template>
</template>

<script>
import leaseMappingService from '@/services/leaseMappingService'
import LeaseMappingForm from './LeaseMappingForm.vue'
import CstPageTemplate from '@/templates/CstPageTemplate.vue'
import HeaderMenu from '@/components/generic/presentational/molecules/headerMenu/HeaderMenu.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'

export default {
  name: 'LeaseMappingPage',
  components: {
    CstPageTemplate,
    HeaderMenu,
    LeaseMappingForm,
    AppButton,
  },
  data() {
    return {
      mappings: [],
      showForm: false,
      deleteConfirmMapping: null,
      deleteInProgress: false,
      loading: false,
      error: null,
    }
  },
  mounted() {
    this.loadMappings()
  },
  methods: {
    async loadMappings() {
      this.loading = true
      this.error = null
      try {
        const response = await leaseMappingService.getAllLeaseMappings()
        this.mappings = response.data || []
      } catch (e) {
        console.error('Failed to load lease mappings:', e)
        this.error = 'Het laden van de lease mappings is mislukt. Probeer het opnieuw.'
      } finally {
        this.loading = false
      }
    },
    onCreate() {
      this.showForm = true
    },
    closeForm() {
      this.showForm = false
    },
    onMappingSaved(mapping) {
      if (mapping) {
        this.mappings.push(mapping)
      }
      this.showForm = false
    },
    onDeleteClick(mapping) {
      this.deleteConfirmMapping = mapping
    },
    async onDeleteConfirm() {
      this.deleteInProgress = true
      try {
        await leaseMappingService.deleteLeaseMapping(
          this.deleteConfirmMapping.DebtorId,
          this.deleteConfirmMapping.DealerId
        )
        this.mappings = this.mappings.filter(
          (m) =>
            !(
              m.DealerId === this.deleteConfirmMapping.DealerId &&
              m.DebtorId === this.deleteConfirmMapping.DebtorId
            )
        )
        this.deleteConfirmMapping = null
      } catch (e) {
        console.error('Failed to delete lease mapping:', e)
        this.error = 'Er is een fout opgetreden bij het verwijderen van de mapping.'
      } finally {
        this.deleteInProgress = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.lease-mapping-page {
  width: 100%;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 1.5rem 0;
  margin-bottom: 1.5rem;

  h1 {
    margin: 0;
    font-size: 1.75rem;
    color: $gray-darker;
  }
}

.content-section {
  background: $white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.loading {
  text-align: center;
  padding: 3rem;
  color: $gray-dark;
  font-size: 1.1rem;
}

.table-scroll-container {
  overflow-y: auto;
  max-height: calc(100vh - 260px);
}

.mappings-table {
  width: 100%;
  border-collapse: collapse;
}

.mappings-table th {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 2px solid $gray-lighter;
  background-color: $gray-lightest;
  color: $gray-darker;
  font-weight: 600;
}

.mappings-table td {
  padding: 0.75rem 1rem;
  text-align: left;
  border-bottom: 1px solid $gray-lighter;
}

.mappings-table tbody tr:hover {
  background-color: $gray-lightest;
}

.actions-col {
  width: 90px;
  text-align: center;
}

.action-buttons {
  display: flex;
  gap: 0.4rem;
  justify-content: center;
}

.btn-action {
  background: none;
  border: 1px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.2rem 0.35rem;
  line-height: 1;
  transition:
    background-color 0.15s,
    border-color 0.15s;
}

.btn-delete:hover {
  background-color: rgba($color-danger, 0.08);
  border-color: $color-danger;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  color: $gray-dark;

  p {
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
}

/* Delete confirmation modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  background: $white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 1.75rem 2rem;
  max-width: 440px;
  width: 90%;
}

.modal-title {
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: $gray-darker;
}

.modal-body {
  margin: 0 0 1.5rem;
  color: $gray-dark;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.error-message {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background-color: $color-danger;
  color: $white;
  padding: 1rem 1.5rem;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 1000;
}

.close-error {
  background: none;
  border: none;
  color: $white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.icon-plus::before {
  content: '+';
}
</style>
